import { Injectable } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus, InteractionType,
} from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

// =============================================================================
// WHICH ACCOUNT IS THIS SESSION? ONE PLACE DECIDES, AND IT NEVER GUESSES.
//
// MSAL caches every account that has ever signed in on this browser profile, in
// localStorage (auth-config.ts, cacheLocation), and keeps them until an explicit
// sign-out. Nothing removed them, so a machine that has used two B2C users holds
// two accounts indefinitely.
//
// Nothing used to PIN one. With no active account set, MsalInterceptor falls back
// to index zero of the cache:
//
//     node_modules/@azure/msal-angular/esm2015/msal.interceptor.js:35-41
//     if (!!this.authService.instance.getActiveAccount()) { ... }
//     else { account = this.authService.instance.getAllAccounts()[0]; }
//
// and AuthTokenService did the same thing directly. Index zero is
// Object.keys(localStorage) order (msal-browser BrowserStorage.js:33-35), which
// is not a stable identity: MSAL rewrites cache entries on token refresh, so the
// order can change underneath a running session.
//
// 2026-09-04: that is exactly what happened. One session, no sign-in and no
// sign-out, served one B2C user's notebooks on Home and a DIFFERENT B2C user's
// notebooks on My Notebooks. Both users were the same person and the same B2C
// tenant, so nothing looked wrong, it just showed the wrong data. On an
// evidentiary product, quietly answering as the wrong identity is the failure
// mode that matters.
//
// THE RULE: the active account is whoever AUTHENTICATED, taken from the
// authentication result. Never from array order. When we genuinely cannot tell,
// we ASK, because silently choosing is what caused the incident.
// =============================================================================
@Injectable({ providedIn: 'root' })
export class ActiveAccountService {

  // Per-TAB guard. The disambiguation below is a redirect, and a redirect that
  // can re-fire on every load is an infinite loop. One attempt per tab: if it
  // somehow fails to pin an account, the app stays usable and degraded rather
  // than bouncing. A new tab may legitimately ask again.
  private static readonly ASKED_KEY = 'fn.activeAccount.disambiguationAsked';

  // Separate from ASKED_KEY and deliberately NOT persisted. A failed or
  // cancelled selection must release the sessionStorage guard so a later
  // navigation can offer the picker again, but releasing it alone would let the
  // very next inProgress=None re-enter reconcile() in the SAME load and redirect
  // immediately: a forced picker the user cannot decline. This in-memory flag
  // holds the re-offer until the next page load.
  private askedThisPageLoad = false;

  /** Emitted after an interactive flow the user actually saw and completed. */
  private static isInteractive(m: EventMessage): boolean {
    return m.interactionType === InteractionType.Redirect
      || m.interactionType === InteractionType.Popup;
  }

  constructor(
    private msal: MsalService,
    private broadcast: MsalBroadcastService,
  ) {
    // PURGE THE OLD PERSISTED GUARD. Tabs that ran the previous build may hold
    // ASKED_KEY='1' in sessionStorage, which is what wedged them. Nothing reads
    // it any more, but clearing it on construction means an already-affected tab
    // recovers on its next reload instead of carrying a dead landmine around.
    this.clearAsked();
    // 1. PIN ON INTERACTION TYPE, NOT ON EVENT NAME.
    //
    //    msal-browser picks the success event by whether the ACCOUNT COUNT
    //    INCREASED, not by whether the user interacted:
    //
    //      ClientApplication.js:146   loggedInAccounts = this.getAllAccounts()
    //      ClientApplication.js:168   isLoggingIn = loggedInAccounts.length < getAllAccounts().length
    //      :169-176                   LOGIN_SUCCESS if so, else ACQUIRE_TOKEN_SUCCESS (Redirect)
    //
    //    The precondition of this entire feature is that BOTH accounts are
    //    already cached. So when the user picks an EXISTING account the count
    //    does not change and their choice arrives as ACQUIRE_TOKEN_SUCCESS. A
    //    guard keyed on the event NAME therefore discarded the one event it
    //    existed to honour, and the tab wedged: no pin, no profile, no re-ask.
    //
    //    interactionType is the honest signal. Redirect and Popup mean a human
    //    completed a flow; Silent means code asked on its own behalf, which is
    //    what MsalInterceptor does off getAllAccounts()[0]
    //    (msal.interceptor.js:41, PublicClientApplication.js:195). So:
    //    interactive completions pin unconditionally, silent ones only once the
    //    identity is no longer ambiguous.
    this.broadcast.msalSubject$
      .pipe(filter((m: EventMessage) =>
        m.eventType === EventType.LOGIN_SUCCESS
        || m.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
        || m.eventType === EventType.SSO_SILENT_SUCCESS))
      .subscribe((m: EventMessage) => {
        const interactive = ActiveAccountService.isInteractive(m);
        if (!interactive && this.isAmbiguous()) {
          console.warn('ActiveAccountService: ignoring a SILENT token acquisition while '
            + 'the account cache is ambiguous; it may be the interceptor\'s index-zero '
            + 'fallback.');
          return;
        }
        const account = (m.payload as AuthenticationResult)?.account;
        if (account) {
          this.pin(account);
        }
      });

    // 1c. A cancelled or failed selection must not strand the tab.
    //
    //     The failure event is chosen the same way, by account count:
    //       ClientApplication.js:182-187
    //       loggedInAccounts.length > 0 -> ACQUIRE_TOKEN_FAILURE, else LOGIN_FAILURE
    //     With two accounts cached that is ALWAYS ACQUIRE_TOKEN_FAILURE, so a
    //     handler watching only LOGIN_FAILURE was dead code and one cancel stood
    //     the tab down permanently.
    //
    //     Only INTERACTIVE failures count. A silent failure is routine (the
    //     interceptor falls back to interaction on its own) and must not release
    //     the guard.
    this.broadcast.msalSubject$
      .pipe(filter((m: EventMessage) =>
        (m.eventType === EventType.ACQUIRE_TOKEN_FAILURE || m.eventType === EventType.LOGIN_FAILURE)
        && ActiveAccountService.isInteractive(m)))
      .subscribe(() => {
        console.warn('ActiveAccountService: account selection did not complete. '
          + 'It will be offered again on the next page load.');
        // This event arrives on the RETURN load, in a FRESH service instance
        // where askedThisPageLoad started false, and msal emits
        // HANDLE_REDIRECT_END from the same catch block immediately afterwards,
        // driving inProgress to None and re-entering reconcile(). Without this
        // line the user's cancel would be overridden before they ever saw the
        // app. The same cycle runs with no cancel at all for any interactive
        // error returned to the redirect URI, AADB2C90118 (Forgot password)
        // being the common one.
        //
        // Suppresses THIS load only. The next document load may offer again,
        // and the in-page escape hatch is available immediately either way.
        this.askedThisPageLoad = true;
      });

    // 2. RECONCILE, but only once MSAL is idle. Doing this while an interaction
    //    is in flight throws interaction_in_progress, and it is also the only
    //    point at which the redirect promise has definitely settled, so a
    //    just-completed sign-in has already pinned itself via (1) above.
    this.broadcast.inProgress$
      .pipe(filter((s: InteractionStatus) => s === InteractionStatus.None))
      .subscribe(() => this.reconcile());
  }

  /**
   * The account this session is acting as, or null if that cannot be answered
   * without guessing. Callers must treat null as "do not proceed", never as
   * "pick one".
   */
  current(): AccountInfo | null {
    const active = this.msal.instance.getActiveAccount();
    if (active) { return active; }

    const accounts = this.msal.instance.getAllAccounts();
    if (accounts.length === 1) {
      // Unambiguous: one cached account is the same answer under every possible
      // ordering, so pinning it is a statement of fact, not a guess.
      this.pin(accounts[0]);
      return accounts[0];
    }

    // Zero (not signed in yet) or two-plus (ambiguous). Both are for reconcile()
    // to act on, and neither may be resolved by array order.
    return null;
  }

  /**
   * Fires once each time an account becomes the pinned identity.
   *
   * AuthService gives up its startup chain when the identity is ambiguous, and
   * nothing else re-runs it: initialize()'s only other trigger is the
   * network-recovery subscription. Without this signal a user who successfully
   * picked an account had an identity and no profile, no clientEncryptionKEK and
   * no hub until they pressed F5, which nothing told them to do.
   */
  private readonly resolvedSource$ = new Subject<AccountInfo>();
  readonly resolved$ = this.resolvedSource$.asObservable();

  /** Set the active account and announce it. The ONLY place that pins. */
  private pin(account: AccountInfo): void {
    this.msal.instance.setActiveAccount(account);
    this.resolvedSource$.next(account);
  }

  /** TRUE when the cache holds several accounts and none of them is pinned. */
  isAmbiguous(): boolean {
    return !this.msal.instance.getActiveAccount()
      && this.msal.instance.getAllAccounts().length > 1;
  }

  /**
   * Settle the active account, or start the one interaction that can settle it.
   *
   * Deliberately does NOT remove or evict any cached account. Clearing a cache
   * entry signs that person out, and the product must never do that on their
   * behalf.
   */
  private reconcile(): void {
    if (this.current()) { return; }              // pinned, or unambiguously one
    if (!this.isAmbiguous()) { return; }         // zero accounts: MsalGuard's job

    // Two or more, none pinned, and no authentication happened in this session
    // to tell us which one the user meant. There is no correct silent answer, so
    // hand the choice to B2C's own account picker rather than inventing one.
    //
    // GUARDED PER PAGE LOAD, IN MEMORY ONLY. This used to also consult a
    // sessionStorage key, which was a wedge rather than a guard: sessionStorage
    // survives reloads within a tab, and the key was released ONLY by an
    // interactive failure event. Any return leg that produced neither a pin nor
    // such an event (closing the B2C tab, navigating away, the back button) left
    // the key set, so reconcile() stood down in that tab FOREVER. Nothing else
    // starts an interactive flow, so nothing could ever pin, so initialize()
    // never re-ran: a permanent infinite spinner with no escape but DevTools.
    // That is what took dev.forensicnotes.com down on 2026-09-04.
    //
    // In-memory is the correct lifetime. It dies with the document, so every
    // fresh load may offer once, and it still blocks a second redirect inside
    // ONE load, which is the same-load loop the persisted key was actually
    // protecting against. An automatic return always carries an msal event, so
    // it cannot cycle unattended; a manual return is the user asking to be shown
    // the app again, and re-offering is the right answer.
    if (this.askedThisPageLoad) {
      console.warn('ActiveAccountService: multiple accounts and none active, '
        + 'and the account picker has already been offered in this page load. '
        + 'Not redirecting again.');
      return;
    }
    this.askedThisPageLoad = true;
    console.warn('ActiveAccountService: multiple cached accounts and none active. '
      + 'Prompting for account selection rather than choosing one.');
    this.startSelection();
  }

  /**
   * Start the account picker NOW, on the user's explicit instruction.
   *
   * Bypasses askedThisPageLoad deliberately: the guard exists to stop the app
   * redirecting on its own, not to stop a person pressing a button. This is the
   * escape hatch from the degraded state, and it must work even if reconcile()
   * has already stood down for this load.
   */
  promptForAccount(): void {
    this.askedThisPageLoad = true;
    this.startSelection();
  }

  private startSelection(): void {
    // Unhandled rejection otherwise: this can race an interceptor-initiated
    // interaction and reject with interaction_in_progress. Left guarded rather
    // than retried, because the in-page escape hatch gives the user a way
    // forward and an automatic retry here risks the loop we just removed.
    this.msal.instance.loginRedirect({
      scopes: [environment.scopes],
      prompt: 'select_account',
    }).catch((e) => {
      console.warn('ActiveAccountService: account selection could not be started.', e);
    });
  }

  /** Release the one-shot guard so selection can be offered again. */
  private clearAsked(): void {
    try { sessionStorage.removeItem(ActiveAccountService.ASKED_KEY); }
    catch { /* non-fatal */ }
  }
}
